import { useCallback, useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Check, Sparkles } from 'lucide-react';
import Layout from '../components/Layout.js';
import { copyText } from '../utils/clipboard.js';
import type { LinkPage } from '../linkpages/themes.js';
import { useLP } from '../linkpages/texts.js';
import { useTier } from '../hooks/useTier.js';
import { UpgradePrompt } from '../components/UpgradePrompt.js';
import { useAppTranslation } from '../hooks/useAppTranslation.js';
import { TIER_LIMITS, TIER_HIERARCHY, type Tier } from '@vcard/shared';

const SLUG_RE = /^[a-z0-9][a-z0-9-]{1,38}[a-z0-9]$/;

export async function readError(res: Response): Promise<string> {
  const body = (await res.json().catch(() => ({}))) as { error?: string };
  return body.error ?? `Request failed (${res.status})`;
}

export default function LinkPages() {
  const lp = useLP();
  const { t } = useAppTranslation();
  const { tier, limits, loading: tierLoading } = useTier();
  const navigate = useNavigate();
  const [pages, setPages] = useState<LinkPage[] | null>(null);
  const [error, setError] = useState('');
  const [slug, setSlug] = useState('');
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [limitHit, setLimitHit] = useState(false);

  const pageLimit = limits.linkPages;
  const atLimit = limitHit || (pages !== null && pages.length >= pageLimit);
  // next tier that allows more link pages (for the upgrade hint)
  const nextTier = TIER_HIERARCHY.find((t2) => TIER_LIMITS[t2].linkPages > pageLimit && t2 !== 'physical') as Tier | undefined;

  const load = useCallback(async () => {
    const res = await fetch('/api/link-pages', { credentials: 'include' });
    if (res.status === 401) { navigate('/login'); return; }
    if (!res.ok) { setError(await readError(res)); return; }
    setPages((await res.json()) as LinkPage[]);
  }, [navigate]);

  useEffect(() => { void load(); }, [load]);

  async function create(e: FormEvent) {
    e.preventDefault();
    setError('');
    const clean = slug.trim().toLowerCase();
    if (!SLUG_RE.test(clean)) {
      setError(lp('slugInvalid'));
      return;
    }
    setCreating(true);
    const res = await fetch('/api/link-pages', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ slug: clean, title: clean }),
    });
    setCreating(false);
    if (res.status === 402) { setLimitHit(true); return; }
    if (!res.ok) { setError(await readError(res)); return; }
    const page = (await res.json()) as LinkPage;
    navigate(`/links/${page.id}`);
  }

  function copyUrl(page: LinkPage) {
    void copyText(`${window.location.origin}/l/${page.slug}`).then((ok) => {
      if (!ok) return;
      setCopied(page.slug);
      setTimeout(() => setCopied(null), 1500);
    });
  }

  return (
    <Layout title={lp('title')}>
      <div className="max-w-5xl space-y-5">
        <p className="text-sm text-ink-muted">
          {lp('intro')}
        </p>

        {!tierLoading && (
          <p className="text-xs text-ink-faint">
            {t('paywall.linkPagesUsage', '{{used}} von {{limit}} Link-Seiten genutzt', { used: pages?.length ?? 0, limit: pageLimit })}
          </p>
        )}

        {atLimit && (
          <UpgradePrompt
            requiredTier={tier === 'enterprise' ? undefined : nextTier}
            message={t('paywall.linkPagesLimit', 'Dein Plan enthält {{limit}} Link-Seite(n). Für mehr brauchst du einen höheren Plan.', { limit: pageLimit })}
          />
        )}

        <form onSubmit={create} className={`flex flex-wrap items-end gap-3 rounded-xl bg-panel border border-line p-5 ${atLimit ? 'opacity-60' : ''}`}>
          <div className="flex-1 min-w-[220px]">
            <label htmlFor="new-slug" className="block text-xs font-semibold uppercase tracking-wide text-ink-muted mb-1">
              {lp('newAddress')}
            </label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-ink-muted">/l/</span>
              <input
                id="new-slug"
                value={slug}
                disabled={atLimit}
                onChange={(e) => setSlug(e.target.value)}
                placeholder={lp('slugPlaceholder')}
                autoComplete="off"
                className="w-full rounded-lg bg-panel border border-line px-3 py-2 text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:border-accent-500/50 focus:ring-1 focus:ring-accent-500/30"
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={creating || atLimit}
            className="rounded-full bg-accent-600 px-5 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
          >
            {lp('createPage')}
          </button>
          <Link
            to="/links/new"
            aria-disabled={atLimit}
            onClick={(e) => { if (atLimit) e.preventDefault(); }}
            className={`inline-flex items-center gap-1.5 rounded-full border border-line px-5 py-2 text-sm font-semibold text-ink-muted hover:bg-raised hover:text-ink ${atLimit ? 'pointer-events-none opacity-50' : ''}`}
          >
            <Sparkles className="h-4 w-4 text-accent-500" />
            {lp('wizCreateWithWizard')}
          </Link>
        </form>

        {error && (
          <div role="alert" className="rounded-lg border border-danger-500/30 bg-danger-100 dark:bg-danger-500/10 px-4 py-3 text-sm text-danger-600 dark:text-danger-300">{error}</div>
        )}

        {pages === null && !error && <div className="h-28 rounded-xl bg-panel border border-line animate-pulse" />}
        {pages && pages.length === 0 && (
          <div className="rounded-xl bg-panel border border-line p-12 text-center text-ink-faint">
            {lp('emptyList')}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {(pages ?? []).map((p) => (
            <div key={p.id} data-testid="lp-list-item" className="rounded-xl bg-panel border border-line p-5 hover:border-accent-500/30 transition-all">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-ink truncate">{p.title}</p>
                  <a href={`/l/${p.slug}`} target="_blank" rel="noopener noreferrer" className="text-xs text-accent-700 dark:text-accent-300 hover:underline">
                    /l/{p.slug}
                  </a>
                </div>
                <div className="text-right text-xs text-ink-muted">
                  <div>{p.views ?? 0} {lp('views')}</div>
                  <div>{p.links.filter((l) => l.type === 'link').length} {lp('linkCount')}</div>
                </div>
              </div>
              <div className="mt-4 flex gap-2">
                <Link
                  to={`/links/${p.id}`}
                  className="flex-1 rounded-lg bg-panel border border-line px-3 py-1.5 text-center text-xs font-medium text-ink-muted hover:bg-raised hover:text-ink"
                >
                  {lp('edit')}
                </Link>
                <button
                  type="button"
                  onClick={() => copyUrl(p)}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-panel border border-line px-3 py-1.5 text-xs text-ink-muted hover:bg-raised"
                >
                  {copied === p.slug ? <><Check className="h-3.5 w-3.5 text-success-500" /> {lp('copied')}</> : lp('copyUrl')}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
}
